import type React from 'react';
import { useState, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@ui/card.tsx';
import { Button } from '@ui/button.tsx';
import { Input } from '@ui/input.tsx';
import { Separator } from '@ui/separator.tsx';
import { toast } from 'sonner';
import { Globe, Facebook, Linkedin, Youtube, Twitter, Instagram, BadgeInfo } from 'lucide-react';

import {
  adminLocales,
  adminWorkspaceConfigs,
  apiAdminlocalescustomIdDelete,
  apiAdminlocalescustomIdPatch,
  apiAdminlocalescustomPost,
  apiAdminworkspaceConfigsIdDelete,
  apiAdminworkspaceConfigsIdPatch,
  apiAdminworkspaceConfigsPost
} from '@r/api/generated/client.ts';
import type {
  LocaleLocaleAdminReadModel,
  WorkspaceConfigWorkspaceConfigReadModel,
  ConfigConfigReadModel
} from '@r/api/generated/client.schemas.ts';
import { useAllConfigs, getConfigsFromResponse, findConfig } from '@r/hooks/use-all-configs.ts';
import { isOrvalErrorFromPath } from '@r/api/orval-error-helper.ts';
import { useUser } from '@r/api/auth.tsx';
import { getActiveWorkspaceUniversal } from '@r/helpers/api.ts';
import { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';

type LinkKey =
  | 'custom-link'
  | 'facebook-link'
  | 'youtube-link'
  | 'linkedin-link'
  | 'x-link'
  | 'instagram-link';

const ALL_LINK_KEYS: LinkKey[] = [
  'custom-link',
  'facebook-link',
  'linkedin-link',
  'youtube-link',
  'x-link',
  'instagram-link'
];
const SOCIAL_LINK_KEYS: LinkKey[] = [
  'facebook-link',
  'linkedin-link',
  'youtube-link',
  'x-link',
  'instagram-link'
];

const EMPTY_VALUES: Record<LinkKey, string> = {
  'custom-link': '',
  'facebook-link': '',
  'linkedin-link': '',
  'youtube-link': '',
  'x-link': '',
  'instagram-link': ''
};

const LABEL_LOCALE_KEY = 'personalization.external.links.custom.link.display';

function extractDomain(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
}

function getLinkIcon(key: LinkKey): React.ElementType {
  switch (key) {
    case 'facebook-link':
      return Facebook;
    case 'linkedin-link':
      return Linkedin;
    case 'youtube-link':
      return Youtube;
    case 'x-link':
      return Twitter;
    case 'instagram-link':
      return Instagram;
    default:
      return Globe;
  }
}

interface LinkFieldProps {
  icon: React.ElementType;
  placeholder: string;
  value: string;
  disabled: boolean;
  saved: boolean;
  onChange: (value: string) => void;
}

const LinkField = ({
  icon: Icon,
  placeholder,
  value,
  disabled,
  saved,
  onChange
}: LinkFieldProps) => (
  <div className="flex gap-4 items-center py-3 w-full">
    <Icon className={`w-5 h-5 shrink-0 ${saved ? 'text-primary' : 'text-muted-foreground'}`} />
    <div className="flex-1 min-w-0">
      <Input
        type="url"
        placeholder={placeholder}
        value={value}
        disabled={disabled}
        clear
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  </div>
);

export const ExternalLinksPage = () => {
  const { t } = useTranslation();
  const showOrvalErrorToast = useOrvalErrorToast();
  const queryClient = useQueryClient();
  const user = useUser().data;
  const activeWorkspace = getActiveWorkspaceUniversal(user?.visibleWorkspaces);
  const roles = activeWorkspace?.roles ?? [];
  const canEdit = roles.includes('ROLE_ADMIN') || roles.includes('ROLE_FIDDY_ADMIN');

  const [values, setValues] = useState<Record<LinkKey, string>>(EMPTY_VALUES);
  const [labelValue, setLabelValue] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const prevDataRef = useRef<WorkspaceConfigWorkspaceConfigReadModel[] | undefined>(undefined);

  const allConfigsQuery = useAllConfigs();
  const configs = useMemo<ConfigConfigReadModel[] | undefined>(
    () => getConfigsFromResponse(allConfigsQuery.data),
    [allConfigsQuery.data]
  );

  const labelLocaleQuery = useQuery<LocaleLocaleAdminReadModel | null>({
    queryKey: ['locale', LABEL_LOCALE_KEY],
    queryFn: async () => {
      const response = await adminLocales({ label: LABEL_LOCALE_KEY });
      return (response.data ?? [])[0] ?? null;
    },
    enabled: canEdit
  });

  useEffect(() => {
    if (!labelLocaleQuery.data) return;
    setLabelValue(labelLocaleQuery.data.isCustom ? labelLocaleQuery.data.content : '');
  }, [labelLocaleQuery.data]);

  const workspaceConfigsQuery = useQuery<WorkspaceConfigWorkspaceConfigReadModel[]>({
    queryKey: ['workspaceConfigs', 'external-links'],
    queryFn: async () => {
      const response = await adminWorkspaceConfigs({ 'config.type': 'link' });
      return (response.data || []) as WorkspaceConfigWorkspaceConfigReadModel[];
    },
    enabled: canEdit
  });

  useEffect(() => {
    if (!workspaceConfigsQuery.data) return;
    if (workspaceConfigsQuery.data === prevDataRef.current) return;
    prevDataRef.current = workspaceConfigsQuery.data;
    const initial = { ...EMPTY_VALUES };
    for (const key of ALL_LINK_KEYS) {
      const wc = workspaceConfigsQuery.data.find((item) => item.config?.key === key);
      if (wc?.value) {
        initial[key] = wc.value;
      }
    }
    setValues(initial);
  }, [workspaceConfigsQuery.data]);

  const getWorkspaceConfigForKey = (key: LinkKey) =>
    workspaceConfigsQuery.data?.find((wc) => wc.config?.key === key);

  const saveLabelLocale = async () => {
    const locale = labelLocaleQuery.data;
    const content = labelValue.trim() || extractDomain(values['custom-link']);

    if (content && !locale?.isCustom && locale?.id) {
      await apiAdminlocalescustomPost({ locale: `/locales/${locale.id}`, content });
      await labelLocaleQuery.refetch();
    } else if (content && locale?.isCustom && locale?.id) {
      await apiAdminlocalescustomIdPatch(locale.id.toString(), { content });
    } else if (!content && locale?.isCustom && locale?.id) {
      await apiAdminlocalescustomIdDelete(locale.id.toString());
      await labelLocaleQuery.refetch();
    }
  };

  const saveSectionInner = async (keys: LinkKey[]) => {
    if (!configs) return;
    for (const key of keys) {
      const config = findConfig(configs, 'link', key);
      if (!config?.id) continue;

      const url = values[key].trim();
      const existing = getWorkspaceConfigForKey(key);

      if (existing?.id) {
        if (url) {
          await apiAdminworkspaceConfigsIdPatch(existing.id.toString(), { value: url });
        } else {
          await apiAdminworkspaceConfigsIdDelete(existing.id.toString());
        }
      } else if (url) {
        await apiAdminworkspaceConfigsPost({ value: url, config: `/configs/${config.id}` });
      }
    }
    await queryClient.invalidateQueries({ queryKey: ['workspaceConfigs'] });
    await workspaceConfigsQuery.refetch();
  };

  const saveCustomLinkSection = async () => {
    setSubmitting(true);
    try {
      await Promise.all([saveSectionInner(['custom-link']), saveLabelLocale()]);
      toast.success(t('admin.translations.edit.form.success'));
    } catch (error) {
      showOrvalErrorToast(error, t('toast.error.label'));
    } finally {
      setSubmitting(false);
    }
  };

  const saveSection = async (keys: LinkKey[]) => {
    setSubmitting(true);
    try {
      await saveSectionInner(keys);
      toast.success(t('admin.translations.edit.form.success'));
    } catch (error) {
      if (
        isOrvalErrorFromPath(error, 'patch', '/admin/workspaceConfigs') ||
        isOrvalErrorFromPath(error, 'post', '/admin/workspaceConfigs')
      ) {
        showOrvalErrorToast(error, t('toast.error.label'));
      } else {
        toast.error(t('toast.error.label'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col space-y-10 grow">
      <div className="space-y-3">
        <h1 className="text-4xl font-bold">{t('personalization.external.links.title')}</h1>
        <p className="font-light text-muted-foreground">
          {t('personalization.external.links.description')}
        </p>
      </div>

      {/* Liens externes */}
      <Card className="w-full">
        <CardHeader>
          <CardTitle className="text-xl font-semibold">
            {t('personalization.external.links.section.custom.title')}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6 pt-0 space-y-6">
          <p className="text-sm text-muted-foreground">
            {t('personalization.external.links.section.custom.description')}
          </p>

          <div className="flex gap-2 items-start p-3 text-sm rounded-lg bg-muted/50 text-muted-foreground">
            <BadgeInfo className="mt-0.5 w-4 h-4 shrink-0" />
            <span>{t('personalization.external.links.custom.hint')}</span>
          </div>

          <div className="space-y-4">
            <div>
              <p className="mb-1 text-sm font-medium">
                {t('personalization.external.links.custom.label')}
              </p>
              <LinkField
                icon={Globe}
                placeholder={t('personalization.external.links.placeholder')}
                value={values['custom-link']}
                disabled={!canEdit || submitting}
                saved={!!getWorkspaceConfigForKey('custom-link')?.value}
                onChange={(value) => setValues((prev) => ({ ...prev, 'custom-link': value }))}
              />
            </div>

            <div>
              <p className="mb-1 text-sm font-medium">
                {t('personalization.external.links.custom.display.label')}
              </p>
              <Input
                type="text"
                placeholder={
                  extractDomain(values['custom-link']) ||
                  t('personalization.external.links.custom.display.placeholder')
                }
                value={labelValue}
                disabled={!canEdit || submitting}
                clear
                onChange={(event) => setLabelValue(event.target.value)}
              />
            </div>
          </div>

          {canEdit && (
            <div className="flex justify-end">
              <Button
                rounded="full"
                size="custom_sm"
                loading={submitting}
                onClick={saveCustomLinkSection}
              >
                {t('personalization.external.links.save')}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Réseaux sociaux */}
      <Card className="w-full">
        <CardHeader>
          <CardTitle className="text-xl font-semibold">
            {t('personalization.external.links.section.social.title')}
          </CardTitle>
        </CardHeader>

        <CardContent className="p-6 pt-0 space-y-6">
          <p className="text-sm text-muted-foreground">
            {t('personalization.external.links.section.social.description')}
          </p>

          <div className="space-y-1">
            {SOCIAL_LINK_KEYS.map((key, index) => {
              const Icon = getLinkIcon(key);
              return (
                <div key={key}>
                  <LinkField
                    icon={Icon}
                    placeholder={t(`personalization.external.links.${key}.placeholder`)}
                    value={values[key]}
                    disabled={!canEdit || submitting}
                    saved={!!getWorkspaceConfigForKey(key)?.value}
                    onChange={(value) => setValues((prev) => ({ ...prev, [key]: value }))}
                  />
                  {index < SOCIAL_LINK_KEYS.length - 1 && <Separator />}
                </div>
              );
            })}
          </div>

          {canEdit && (
            <div className="flex justify-end">
              <Button
                rounded="full"
                size="custom_sm"
                loading={submitting}
                onClick={() => saveSection(SOCIAL_LINK_KEYS)}
              >
                {t('personalization.external.links.save')}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {!canEdit && (
        <p className="text-sm text-muted-foreground">
          {t('personalization.external.links.access.denied')}
        </p>
      )}
    </div>
  );
};
